import React, { useEffect, useState } from 'react';
import { G, styles, api } from '../theme.js';
import { Btn, Badge, Spinner } from '../ui.jsx';
import { BrandVoiceWizard } from './BrandVoiceWizard.jsx';
import { TechInviteWizard } from './TechInviteWizard.jsx';

// Streamlined onboarding panel that lives at the TOP of the Dashboard until
// the owner has done the 3 highest-leverage setup steps:
//   1. Provision a real phone number (so SMS + AI calls go to real customers)
//   2. Connect Stripe (so they can take payments)
//   3. Import contacts (so they have real people to message)
//
// Anything else (Calendar / GBP / QuickBooks / branding) is deferred to the
// in-context moment the owner first reaches for that feature.
//
// Auto-hides once all 3 are done. Compact + dismissible while showing a
// "you can come back to this later" affordance.

const STEPS = [
  {
    id: 'phone',
    icon: '📱',
    title: 'Connect your phone number',
    why: 'Your AI agent answers + texts customers. Takes 30 seconds — we provision it for you.',
    cta: 'Provision number',
    goto: 'integrations',
    done: (s) => !!s?.account?.twilio_phone,
  },
  {
    id: 'stripe',
    icon: '💳',
    title: 'Connect Stripe to take payments',
    why: 'Send payment links, take card + ACH in-app, get paid same-day. ~3 minutes on Stripe.',
    cta: 'Start Stripe setup',
    goto: 'integrations',
    done: (s) => !!s?.account?.stripe_account_id,
  },
  {
    id: 'contacts',
    icon: '👥',
    title: 'Import your customers',
    why: 'Drop a CSV from QuickBooks / ServiceTitan / Jobber, or paste a list. We dedupe + enrich.',
    cta: 'Import contacts',
    goto: 'contacts',
    done: (s) => (s?.realContactCount || 0) >= 5,
  },
];

export function QuickStartCard({ go = () => {}, toast = () => {} }) {
  const [data, setData] = useState(null);
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem('tradeops:quickstart_dismissed') === '1'; } catch { return false; }
  });

  const load = () => api('/api/settings/setup-checklist').then(setData).catch(()=>{});
  useEffect(() => {
    load();
    // Refresh on tab focus so connecting Stripe in another tab updates the bar
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  if (!data) return null;

  // Pull out the real-vs-demo contact count to know whether the import step is done.
  // The setup-checklist endpoint already returns contactCount; we just trust it
  // and let the auto-purge handle the demo cleanup once Stripe + Twilio land.
  const stripeStep   = data.steps?.find(s => s.id === 'stripe');
  const phoneStep    = data.steps?.find(s => s.id === 'phone');
  const contactsStep = data.steps?.find(s => s.id === 'contacts');

  const checks = [
    { ...STEPS[0], isDone: !!phoneStep?.done },
    { ...STEPS[1], isDone: !!stripeStep?.done },
    { ...STEPS[2], isDone: !!contactsStep?.done },
  ];
  const doneCount = checks.filter(s => s.isDone).length;
  const pct = Math.round((doneCount / checks.length) * 100);

  if (doneCount === checks.length) return null; // all done, hide
  if (dismissed) return (
    <div style={{
      ...styles.card, padding:'8px 12px', marginBottom:14, fontSize:12, color:G.textMuted,
      display:'flex', alignItems:'center', justifyContent:'space-between',
    }}>
      <span>🚀 Setup paused ({doneCount}/{checks.length} done). <a href="#" onClick={(e)=>{e.preventDefault(); setDismissed(false); try{localStorage.removeItem('tradeops:quickstart_dismissed');}catch{}}} style={{color:G.purple, fontWeight:600, textDecoration:'none'}}>Resume →</a></span>
    </div>
  );

  return <div style={{ ...styles.card, padding:'18px 20px', marginBottom:14, borderColor:G.purple+'66' }}>
    <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:14, marginBottom:14 }}>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:4 }}>
          <span style={{ fontSize:22 }}>🚀</span>
          <h2 style={{ margin:0, fontSize:18, fontWeight:800 }}>Get to your first real customer</h2>
          <Badge c={G.purple} bg={G.purpleDim}>{doneCount}/{checks.length}</Badge>
        </div>
        <div style={{ fontSize:13, color:G.textMuted, marginBottom:10 }}>
          Three steps to go live. Everything else can wait — we've pre-loaded sample data so you can play with the AI right now.
        </div>
        <div style={{ height:6, background:G.input, borderRadius:3, overflow:'hidden' }}>
          <div style={{ height:'100%', width:`${pct}%`, background:`linear-gradient(90deg, ${G.purple}, ${G.cyan})`, transition:'width .3s' }}/>
        </div>
      </div>
      <button onClick={()=>{ setDismissed(true); try { localStorage.setItem('tradeops:quickstart_dismissed','1'); } catch {} }}
        title="Hide for now — link stays in the corner"
        style={{ background:'transparent', border:'none', cursor:'pointer', color:G.textMuted, fontSize:18, padding:4 }}>×</button>
    </div>

    <div style={{ display:'grid', gap:10 }}>
      {checks.map(step => (
        <div key={step.id} style={{
          display:'flex', alignItems:'center', gap:12, padding:'12px 14px',
          background: step.isDone ? G.greenLight : G.input,
          border:`1px solid ${step.isDone ? G.green+'44' : G.cardBorder}`,
          borderRadius:8,
        }}>
          <span style={{ fontSize:22, opacity: step.isDone ? 0.7 : 1 }}>{step.isDone ? '✅' : step.icon}</span>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontWeight:700, fontSize:14, color: step.isDone ? G.green : G.text, textDecoration: step.isDone ? 'line-through' : 'none' }}>
              {step.title}
            </div>
            {!step.isDone && <div style={{ fontSize:12, color:G.textMuted, marginTop:2 }}>{step.why}</div>}
          </div>
          {!step.isDone && <Btn v="primary" sz="sm" onClick={()=>go?.(step.goto)}>{step.cta} →</Btn>}
        </div>
      ))}
    </div>

    <div style={{ marginTop:12, fontSize:11, color:G.textMuted, display:'flex', justifyContent:'space-between', flexWrap:'wrap', gap:8 }}>
      <span>💡 Sample contacts + a demo job + a paid invoice are pre-loaded so you can test SMS / calls / the AI right now.</span>
      <span>Auto-cleared once you connect real Stripe + phone.</span>
    </div>

    {/* Optional accelerators — make the AI agent + team setup take 60 seconds each */}
    <div style={{ marginTop:12, paddingTop:12, borderTop:`1px dashed ${G.cardBorder}`, display:'flex', gap:8, flexWrap:'wrap' }}>
      <Btn sz="sm" v="ghost" onClick={()=>setVoiceOpen(true)}>🎙️ Teach AI your voice (60s)</Btn>
      <Btn sz="sm" v="ghost" onClick={()=>setInviteOpen(true)}>👥 Invite your team by text</Btn>
    </div>

    <BrandVoiceWizard open={voiceOpen} onClose={()=>setVoiceOpen(false)} onApplied={load} toast={toast}/>
    <TechInviteWizard open={inviteOpen} onClose={()=>setInviteOpen(false)} onDone={load} toast={toast}/>
  </div>;
}
